import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import { counties } from '../data/countyData';

const ServiceAreas = () => {
  const cities = [
    { name: "Orlando", slug: "orlando" },
    { name: "Daytona Beach", slug: "daytona-beach" },
    { name: "Deltona", slug: "deltona" },
    { name: "Sanford", slug: "sanford" },
    { name: "DeLand", slug: "deland" },
    { name: "New Smyrna Beach", slug: "new-smyrna-beach" },
    { name: "Port Orange", slug: "port-orange" },
    { name: "Ormond Beach", slug: "ormond-beach" },
    { name: "Winter Park", slug: "winter-park" },
    { name: "Lake Mary", slug: "lake-mary" },
    { name: "Lake Helen", slug: "lake-helen" },
    { name: "The Villages", slug: "the-villages" }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: { 
      opacity: 1, y: 0, scale: 1,
      transition: { duration: 0.5, ease: "easeOut" as const }
    }
  };

  const headerVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { 
      opacity: 1, y: 0,
      transition: { duration: 0.6, ease: "easeOut" as const }
    }
  };

  return (
    <section className="py-12 bg-gradient-to-br from-navy to-navy/90">
      <div className="container-custom">
        <motion.div 
          className="text-center mb-10"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={headerVariants}
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <MapPin className="w-8 h-8 text-orange" />
            <h2 className="text-white mb-0">Areas We Serve</h2>
          </div>
          <p className="max-w-3xl mx-auto text-white/90 text-lg">
            Elevation Electrical, LLC proudly serves Lake Helen and surrounding cities within a 50-mile radius, 
            providing expert electrical services throughout Central Florida.
          </p>
        </motion.div>
        
        {/* Cities Grid */}
        <motion.div 
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={containerVariants}
        >
          {cities.map((city) => (
            <motion.div
              key={city.slug}
              variants={itemVariants}
              whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
              whileTap={{ scale: 0.98 }}
            >
              <Link to={`/locations/${city.slug}`} className="block">
                <div className="bg-white/10 hover:bg-orange border border-white/20 hover:border-orange rounded-lg p-4 text-center transition-all duration-300 hover:shadow-lg h-full">
                  <span className="text-white font-semibold text-sm">{city.name}</span>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        {/* View All Button */}
        <motion.div 
          className="text-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          <Link to="/locations">
            <motion.button 
              className="bg-orange hover:bg-orange-dark text-navy font-bold py-3 px-8 rounded-lg transition-colors inline-flex items-center gap-2"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <MapPin className="w-5 h-5" />
              View All Service Areas
            </motion.button>
          </Link>
        </motion.div>

        {/* Counties We Serve */}
        <motion.div
          className="border-t border-white/20 pt-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <h3 className="text-white text-center text-lg font-semibold mb-2">Counties We Serve</h3>
          <p className="text-white/70 text-center text-sm mb-5 max-w-2xl mx-auto">
            Our licensed electricians cover four Central Florida counties, delivering consistent quality from coast to countryside.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {counties.map((county) => (
              <Link
                key={county.slug}
                to={`/locations/counties/${county.slug}`}
                className="bg-white/10 hover:bg-orange border border-white/20 hover:border-orange hover:text-navy text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-all duration-300"
              >
                {county.name}
              </Link>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServiceAreas;
